import logoImage from "@/assets/logo.png";
import { ModalSelecaoEmpresa } from "@/components/ModalSelecaoEmpresa";

import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const EMPRESA_TOKEN_KEY = "empresa_token";

interface Empresa {
  id: number;
  nome: string;
  nome_fantasia: string;
  cnpj: string;
  logo?: string;
  tipo_empresa: "MATRIZ" | "FILIAL";
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { signIn, loading, user } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [mostrarModalEmpresa, setMostrarModalEmpresa] = useState(false);
  const [loadingEmpresa, setLoadingEmpresa] = useState(false);
  const [tenantLogo, setTenantLogo] = useState<string | null>(null);

  // Token na URL: salva no localStorage e remove da URL (token na URL tem prioridade para trocar contexto)
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl && tokenFromUrl.trim()) {
      sessionStorage.setItem(EMPRESA_TOKEN_KEY, tokenFromUrl.trim());
      const next = new URLSearchParams(searchParams);
      next.delete("token");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Carrega logo da empresa baseada no token ou no domínio
  useEffect(() => {
    const fetchCompanyLogo = async () => {
      const token = sessionStorage.getItem(EMPRESA_TOKEN_KEY);

      try {
        if (token) {
          const response = await api.get(`/api/empresas/por-token?token=${token}`);
          if (response?.result?.logo) {
            setTenantLogo(response.result.logo);
            return; // Se encontrou pelo token, já sai
          }
        }

        // Se não tem token ou o token não retornou logo, tenta buscar pelo domínio atual
        const hostname = window.location.hostname;
        // Não busca se for um IP local ou localhost padrão, opcional (mas o backend já retorna 404 se não achar)
        if (hostname) {
          const response = await api.get(`/api/empresas/por-dominio?dominio=${hostname}`);
          if (response?.result?.logo) {
            setTenantLogo(response.result.logo);
          }
        }
      } catch (err) {
        console.error("Erro ao buscar logo da empresa:", err);
      }
    };
    fetchCompanyLogo();
  }, []);

  // Redireciona automaticamente se já estiver autenticado
  useEffect(() => {
    if (user?.id_empresa) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSelecionarEmpresa = async (empresaId: number) => {
    try {
      setLoadingEmpresa(true);

      // Chama endpoint para selecionar empresa e obter novo token
      const response = await api.post("/api/auth/selecionar-empresa", {
        id_empresa: empresaId,
      });

      if (response?.result?.token) {
        // Salva o novo token
        sessionStorage.setItem("auth_token", response.result.token);

        // Recarrega os dados do usuário com o novo token
        const empresaSelecionada = empresas.find((e) => e.id === empresaId);

        // Atualiza o user no localStorage com id_empresa
        const storedUser = sessionStorage.getItem("auth_user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          userData.id_empresa = empresaId;
          sessionStorage.setItem("auth_user", JSON.stringify(userData));
        }

        // Navega para o dashboard
        navigate("/dashboard", { replace: true });
        window.location.reload(); // Recarrega para aplicar mudanças
      }
    } catch (err: any) {
      setError(err?.message || "Erro ao selecionar empresa");
    } finally {
      setLoadingEmpresa(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const empresaToken = sessionStorage.getItem(EMPRESA_TOKEN_KEY) || undefined;
      const result = await signIn({
        username: identifier,
        password,
        rememberMe,
        ...(empresaToken ? { empresa_token: empresaToken } : {})
      });

      // Verifica se o token já tem id_empresa (empresa única foi selecionada automaticamente)
      if (result?.user?.id_empresa) {
        // Token já tem empresa, navega direto
        navigate("/dashboard");
      } else if (result?.empresas && result.empresas.length > 1) {
        // Múltiplas empresas: mostra modal
        setEmpresas(result.empresas);
        setMostrarModalEmpresa(true);
      } else if (result?.empresas && result.empresas.length === 1) {
        // Uma empresa: seleciona automaticamente (fallback caso backend não tenha incluído id_empresa)
        await handleSelecionarEmpresa(result.empresas[0].id);
      } else {
        // Sem empresas: navega direto (compatibilidade com código antigo)
        navigate("/dashboard");
      }
    } catch (err: any) {
      console.error("Erro no login:", err);
      const errorMessage = err?.message || "Falha no login";
      setError(errorMessage);

      // Se for erro de conexão, mostra mensagem mais clara
      if (errorMessage.includes("não foi possível conectar") || errorMessage.includes("Failed to fetch")) {
        setError("Não foi possível conectar ao servidor. Verifique se o backend está rodando.");
      }
    }
  };

  return (
    <>
      <ModalSelecaoEmpresa
        open={mostrarModalEmpresa}
        empresas={empresas}
        onSelect={handleSelecionarEmpresa}
        loading={loadingEmpresa}
      />

      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "#f6f7f9" }}>
        <div style={{ width: "100%", maxWidth: 420, background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            {tenantLogo ? (
              <img
                src={tenantLogo.startsWith('data:') ? tenantLogo : `data:image/png;base64,${tenantLogo}`}
                alt="Logo da Empresa"
                style={{ maxWidth: "200px", maxHeight: "100px", width: "auto", height: "auto", margin: "0 auto 16px auto", display: "block", objectFit: "contain" }}
              />
            ) : (
              <img
                src={logoImage}
                alt="Instituto Elisedape"
                style={{ maxWidth: "200px", height: "auto", margin: "0 auto 16px auto", display: "block" }}
              />
            )}
          </div>
          <form onSubmit={onSubmit}>
            <div style={{ display: "grid", gap: 12 }}>
              <label>
                <div style={{ fontSize: 14, marginBottom: 6 }}>Usuário</div>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8 }}
                />
              </label>

              <label>
                <div style={{ fontSize: 14, marginBottom: 6 }}>Senha</div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8 }}
                />
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                <span style={{ fontSize: 13, color: "#374151" }}>Lembrar de mim</span>
              </label>

              {error && (
                <div style={{ color: "#b91c1c", background: "#fee2e2", border: "1px solid #fecaca", padding: 8, borderRadius: 8 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{ width: "100%", padding: "10px 12px", background: "#2563eb", color: "white", border: 0, borderRadius: 8, fontWeight: 600, cursor: "pointer" }}
              >
                {loading ? "Entrando..." : "Entrar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
